import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

// Shared activity log. Server API responses carry the log entries they produced
// (the stateless pattern); the API client pushes them here and this panel shows
// the combined timeline — the web equivalent of the desktop Log window and the
// Go tool's logs/ files.

export type LogLevel = 'debug' | 'info' | 'success' | 'warning' | 'error'
export interface LogEntry {
  ts: string
  level: LogLevel
  category: string
  message: string
  body?: string // raw upstream response body (server-attached), previewable
}

interface ActivityLogState {
  entries: LogEntry[]
  push: (entries: LogEntry[]) => void
  clear: () => void
}

const Ctx = createContext<ActivityLogState | null>(null)

export function ActivityLogProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<LogEntry[]>([])
  const push = useCallback((newEntries: LogEntry[]) => {
    if (!newEntries?.length) return
    setEntries((prev) => [...prev, ...newEntries].slice(-500))
  }, [])
  const clear = useCallback(() => setEntries([]), [])
  const value = useMemo(() => ({ entries, push, clear }), [entries, push, clear])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useActivityLog(): ActivityLogState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useActivityLog must be used inside <ActivityLogProvider>')
  return v
}

export function ActivityLogPanel() {
  const { entries, clear } = useActivityLog()
  return (
    <aside className="activity-log">
      <div className="activity-log-header">
        <span className="alh-title">Activity readout</span>
        {entries.length > 0 && <span className="alh-count">{entries.length}</span>}
        <button className="alh-clear" onClick={clear} disabled={!entries.length}>Clear</button>
      </div>
      <div className="activity-log-body">
        {entries.length === 0 && (
          <p className="alh-empty">Waiting for activity. Every API call streams here as it runs.</p>
        )}
        {entries.map((e, i) => (
          <LogLine key={i} entry={e} />
        ))}
      </div>
    </aside>
  )
}

function prettyBody(body: string): { text: string; isJson: boolean } {
  try {
    return { text: JSON.stringify(JSON.parse(body), null, 2), isJson: true }
  } catch {
    return { text: body, isJson: false }
  }
}

function LogLine({ entry: e }: { entry: LogEntry }) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const pretty = useMemo(() => (open && e.body ? prettyBody(e.body) : null), [open, e.body])
  const copy = () => {
    if (!pretty) return
    navigator.clipboard?.writeText(pretty.text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    }, () => {})
  }
  return (
    <div className={`log-line log-${e.level}`}>
      <span className="log-time">{e.ts.slice(11, 19)}</span>
      <span className="log-cat">{e.category}</span>
      <span className="log-msg">
        {e.message}
        {e.body && (
          <button className="log-body-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {open ? '▾' : '▸'} body
          </button>
        )}
      </span>
      {pretty && (
        <div className="log-body">
          <div className="log-body-bar">
            <span>{pretty.isJson ? 'JSON' : 'text'} · {e.body!.length.toLocaleString()} bytes</span>
            <button onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
          <pre>{pretty.text}</pre>
        </div>
      )}
    </div>
  )
}
